import {ActionInnerMessage, ResponseInnerMessage} from '@/hosted_fields/common/types';
import Assert from '@/helpers/asserts';
import IframeClient, {MASTER_IFRAME_NAME} from '@/hosted_fields/common/connection/client';
import Errors, {CbError} from '@/hosted_fields/common/errors';
import ApiClient from '@/plugins/core/api/api-client';
import t from '@/hosted_fields/common/locale';
import {setFrameLoaded} from '@/hosted_fields/common/dom-utils';
import '@/helpers/polyfills';
import CbInstanceOptions, {MasterInitOptions} from '@/interfaces/cb-instance-options';
import Logger from '@/utils/logger_old';
import EnvConstants, {debugMode} from '@/constants/environment';

import PluginManager from '@/plugins/core/plugin-manager';
import {ApiClientInterface} from '@/plugins/core/api/interface';
import PluginInterface, {PluginName} from '@/plugins/core/interface';

import ComponentsAndFieldsPluginInterface from '@/plugins/components_fields/actions/interface';
import LoggerPluginInterface from '@/plugins/logger/actions/interface';
import ThreeDSPluginInterface from '@/plugins/three_domain_secure/actions/interface';
import FunctionsPluginInterface from '@/plugins/functions/actions/interface';

import {Metadata} from '@/plugins/logger/types';
import CommManagerInterface from '@/hosted_fields/master/interface';
import CheckoutUtilsPluginInterface from '@/plugins/checkout_utils/actions/interface';
import IDealPluginInterface from '@/plugins/payments/iDeal/actions/interface';
import SofortPluginInterface from '@/plugins/payments/sofort/actions/interface';
import KbcPaymentButtonPluginInterface from '@/plugins/payments/kbc_payment_button/actions/interface';
import ElectronicPaymentStandardPluginInterface from '@/plugins/payments/electronic_payment_standard/actions/interface';
import TrustlyPluginInterface from '@/plugins/payments/trustly/actions/interface';
import PayByBankPluginInterface from '@/plugins/payments/pay_by_bank/actions/interface';
import StablecoinPluginInterface from '@/plugins/payments/stablecoin/actions/interface';
import BancontactPluginInterface from '@/plugins/payments/bancontact/actions/interface';
import GooglePayPluginInterface from '@/plugins/payments/google_pay/actions/interface';
import GiropayPluginInterface from '@/plugins/payments/giropay/actions/interface';
import DotpayPluginInterface from '@/plugins/payments/dotpay/actions/interface';
import DirectDebitPluginInterface from '@/plugins/payments/direct_debit/actions/interface';
import ApplePayPluginInterface from '@/plugins/payments/apple_pay/actions/interface';
import AmazonayPluginInterface from '@/plugins/payments/amazon_payments/actions/interface';
import {CaptchaPluginInterface, RecaptchaOptions} from '@/plugins/captcha/types';
import FasterPymtsPluginInterface from '@/plugins/payments/faster_payments/actions/interface';
import PayToPluginInterface from '@/plugins/payments/pay_to/actions/interface';
import SepaInstantTransferPluginInterface from '@/plugins/payments/sepa_instant_transfer/actions/interface';

window.name = MASTER_IFRAME_NAME;
setFrameLoaded(MASTER_IFRAME_NAME);

export default class Master implements CommManagerInterface {
  static _inst: Master;
  public hostName: string;
  public site: string;
  public publishableKey: string;
  public businessEntityId: string;
  public connectionClient: IframeClient;

  public apiClient: ApiClientInterface;
  public pluginMan: PluginManager;
  public site_meta: {
    window_url?: string;
    options?: CbInstanceOptions;
  };

  public piWorker: Worker;

  static instance() {
    return this._inst;
  }

  public loadPlugin(plugin: PluginName): Promise<PluginInterface> {
    return this.pluginMan.registerPlugin(plugin);
  }

  public getComponentsAndFieldsPlugin(): Promise<ComponentsAndFieldsPluginInterface> {
    return <Promise<ComponentsAndFieldsPluginInterface>>this.getPlugin(PluginName.COMPONENTS_FIELDS);
  }

  public getThreeDSPlugin(): Promise<ThreeDSPluginInterface> {
    return <Promise<ThreeDSPluginInterface>>this.getPlugin(PluginName.THREE_DOMAIN_SECURE);
  }

  public getLoggerPlugin(): Promise<LoggerPluginInterface> {
    return <Promise<LoggerPluginInterface>>this.getPlugin(PluginName.LOGGER);
  }

  public getCheckoutUtilsPlugin(): Promise<CheckoutUtilsPluginInterface> {
    return <Promise<CheckoutUtilsPluginInterface>>this.getPlugin(PluginName.CHECKOUT_UTILS);
  }

  public getFunctionsPlugin(): Promise<FunctionsPluginInterface> {
    return <Promise<FunctionsPluginInterface>>this.getPlugin(PluginName.FUNCTIONS);
  }

  public getIdealPlugin(): Promise<IDealPluginInterface> {
    return <Promise<IDealPluginInterface>>this.getPlugin(PluginName.IDEAL);
  }

  public getSofortPlugin(): Promise<SofortPluginInterface> {
    return <Promise<SofortPluginInterface>>this.getPlugin(PluginName.SOFORT);
  }

  public getPayByBankPlugin(): Promise<PayByBankPluginInterface> {
    return <Promise<PayByBankPluginInterface>>this.getPlugin(PluginName.PAY_BY_BANK);
  }

  public getTrustlyPlugin(): Promise<TrustlyPluginInterface> {
    return <Promise<TrustlyPluginInterface>>this.getPlugin(PluginName.TRUSTLY);
  }

  public getElectronicPaymentStandardPlugin(): Promise<ElectronicPaymentStandardPluginInterface> {
    return <Promise<ElectronicPaymentStandardPluginInterface>>this.getPlugin(PluginName.ELECTRONIC_PAYMENT_STANDARD);
  }

  public getKbcPaymentButtonPlugin(): Promise<KbcPaymentButtonPluginInterface> {
    return <Promise<KbcPaymentButtonPluginInterface>>this.getPlugin(PluginName.KBC_PAYMENT_BUTTON);
  }

  public getStablecoinPlugin(): Promise<StablecoinPluginInterface> {
    return <Promise<StablecoinPluginInterface>>this.getPlugin(PluginName.STABLECOIN);
  }

  public getBancontactPlugin(): Promise<BancontactPluginInterface> {
    return <Promise<BancontactPluginInterface>>this.getPlugin(PluginName.BANCONTACT);
  }

  public getGooglePayPlugin(): Promise<GooglePayPluginInterface> {
    return <Promise<GooglePayPluginInterface>>this.getPlugin(PluginName.GOOGLE_PAY);
  }

  public getGiropayPlugin(): Promise<GiropayPluginInterface> {
    return <Promise<GiropayPluginInterface>>this.getPlugin(PluginName.GIROPAY);
  }

  public getDotpayPlugin(): Promise<DotpayPluginInterface> {
    return <Promise<DotpayPluginInterface>>this.getPlugin(PluginName.DOTPAY);
  }

  public getFasterPaymentsPlugin(): Promise<FasterPymtsPluginInterface> {
    return <Promise<FasterPymtsPluginInterface>>this.getPlugin(PluginName.FASTER_PAYMENTS);
  }

  public getPayToPlugin(): Promise<PayToPluginInterface> {
    return <Promise<PayToPluginInterface>>this.getPlugin(PluginName.PAY_TO);
  }

  public getSepaInstantTransferPlugin(): Promise<SepaInstantTransferPluginInterface> {
    return <Promise<SepaInstantTransferPluginInterface>>this.getPlugin(PluginName.SEPA_INSTANT_TRANSFER);
  }

  public getDirectDebitPlugin(): Promise<DirectDebitPluginInterface> {
    return <Promise<DirectDebitPluginInterface>>this.getPlugin(PluginName.DIRECT_DEBIT);
  }

  public getApplePayPlugin(): Promise<ApplePayPluginInterface> {
    return <Promise<ApplePayPluginInterface>>this.getPlugin(PluginName.APPLE_PAY);
  }

  public getCaptchaPlugin(): Promise<CaptchaPluginInterface> {
    return <Promise<CaptchaPluginInterface>>this.getPlugin(PluginName.CAPTCHA);
  }

  public getAmazonPayPlugin(): Promise<AmazonayPluginInterface> {
    return <Promise<AmazonayPluginInterface>>this.getPlugin(PluginName.AMAZON_PAYMENTS);
  }

  public getPlugin(pluginName: PluginName): Promise<PluginInterface> {
    if (this.pluginMan.isPluginLoaded(pluginName)) {
      return this.pluginMan.getPlugin(pluginName);
    } else {
      return this.loadPlugin(pluginName);
    }
  }

  private static checkHost(hostURL: string) {
    if (hostURL) {
      const urlObj = new URL(hostURL);
      let passedHost = urlObj.host;
      const originHost = decodeURIComponent(window.location.hash.slice(1));
      if (urlObj.protocol === 'https:' && passedHost.endsWith(':443')) {
        passedHost = passedHost.replace(':443', '');
      }
      if (passedHost !== originHost) {
        const error = new CbError(t(Errors.unknownMessageOrigin));
        Logger.throw(error, {originHost, passedHost});
      }
    }
  }
  /** @internal */
  static createOne(iframeClient: IframeClient, data: MasterInitOptions) {
    Assert.notTrue(() => !this._inst, t(Errors.instanceAlreadyExists));
    if (!data.publishableKey) {
      // console.warn(t(Errors.missingPublishableKey))
    }
    // Assert.notTrue(() => data.publishableKey, t(Errors.missingPublishableKey));
    this._inst = new Master();
    this._inst.publishableKey = data.publishableKey;
    this._inst.businessEntityId = data.businessEntityId;
    this._inst.hostName = data.host;
    // !TODO we might be validating the host name with the allowed list
    // TODO revisit and refactor;
    this.checkHost(data.host);
    window['hostName'] = data.host;
    this._inst.connectionClient = iframeClient;
    this._inst.site = data.site;
    this._inst.site_meta = {
      window_url: data.window_url,
      options: data.options,
    };
    this._inst.apiClient = new ApiClient(this._inst);
    this._inst.pluginMan = new PluginManager(this._inst);
    // @ts-ignore
    this._inst.piWorker = new Worker(`${EnvConstants.ASSET_PATH}/pi-worker.js`);
    this._inst.initialize();
  }

  initialize(): Promise<any> {
    window['cb_site_info'] = this.getSiteInfo();
    // Initialize logger
    return Promise.all([this.initializeLogger(), this.loadPlugin(PluginName.CHECKOUT_UTILS)]);
  }

  private async handleCaptchaError(error: Error, step?: string) {
    if (debugMode()) console.error('recaptcha error', step, error);
    const captchaPlugin = await this.getCaptchaPlugin();
    const errors = captchaPlugin.getErrors();
    if (errors.length > 0) {
      const latestError = errors[errors.length - 1];
      if (latestError) {
        const logger = await this.getLoggerPlugin();
        const errorMessage = (latestError.cb_retry_error && latestError.cb_retry_error.message) || '';
        const errorName = (latestError.cb_retry_error && latestError.cb_retry_error.name) || '';
        logger.captureKVL({
          action: 'cbjs_recaptcha_error',
          error_message: errorMessage,
          error_name: errorName,
          hp_step: step,
          captcha_config: captchaPlugin.options,
          captcha_error_history: errors,
        });
      }
    }

    return undefined;
  }

  async initializeCaptcha(options: RecaptchaOptions) {
    try {
      const captchaPlugin = await this.getCaptchaPlugin();
      const result = await captchaPlugin.initialize(options);
      return result;
    } catch (e) {
      await this.handleCaptchaError(e, 'captcha_initialization');
    }
  }

  async generateCaptchaToken(action: string): Promise<string | undefined> {
    try {
      const captchaPlugin = await this.getCaptchaPlugin();
      const token = await captchaPlugin.generateToken(action);
      return token;
    } catch (e) {
      await this.handleCaptchaError(e, 'captcha_token_generation');
    }
  }

  private initializeLogger() {
    Logger.init(this.apiClient);
    return this.loadPlugin(PluginName.LOGGER);
  }

  private getHostFromURL(url: string): string {
    try {
      return new URL(url).host;
    } catch (e) {
      return url;
    }
  }

  public getSiteInfo(): Metadata {
    return {
      host_name: this.hostName,
      custom_domain: this.getHostFromURL(this.hostName),
      site_meta: this.site_meta,
      site: this.site,
      domain: this.site,
    };
  }

  public isTestSite(): boolean {
    return this.site.endsWith('-test');
  }

  // Any plugin can use this method to invoke an action in master
  public invokeAction(message: ActionInnerMessage): Promise<ResponseInnerMessage> {
    return this.connectionClient.sendMessage(message, MASTER_IFRAME_NAME);
  }

  public setBusinessEntityAction(message: ActionInnerMessage): Promise<ResponseInnerMessage> {
    this.businessEntityId = message && message.data && message.data.businessEntityId;
    return Promise.resolve({acknowledged: true});
  }

  constructor() {}
}
